import { CalendarClock, Infinity as InfinityIcon } from 'lucide-react';
import {
  type SlideSchedule,
  WEEKDAY_LABELS,
  describeSchedule,
  isEmptySchedule,
  scheduleState,
} from '@/lib/slideSchedule';

interface Props {
  value: SlideSchedule | null;
  onChange: (s: SlideSchedule | null) => void;
}

function todayIso(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Atalhos preenchem os campos (não são um modo à parte) — o usuário clica,
// vê o formulário preenchido e ajusta se quiser.
const PRESETS: { label: string; build: () => SlideSchedule }[] = [
  { label: 'Só hoje', build: () => ({ start: todayIso(), end: todayIso() }) },
  { label: 'Próximos 7 dias', build: () => ({ start: todayIso(), end: todayIso(6) }) },
  { label: 'Fim de semana', build: () => ({ days: [0, 6] }) },
  { label: 'Dias úteis', build: () => ({ days: [1, 2, 3, 4, 5] }) },
];

const STATE_BADGE = {
  live: { text: 'No ar agora', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  idle: { text: 'Fora do ar hoje', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
  upcoming: { text: 'Ainda vai começar', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  expired: { text: 'Já expirou', cls: 'bg-red-100 text-red-700 border-red-200' },
  always: { text: 'No ar agora', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
} as const;

export function ScheduleEditor({ value, onChange }: Props) {
  const scheduled = !isEmptySchedule(value);
  const s = value ?? {};
  const set = (patch: Partial<SlideSchedule>) => onChange({ ...s, ...patch });

  const state = scheduleState(value);
  const badge = STATE_BADGE[state.kind];
  const days = s.days ?? [];
  const hasTime = !!(s.from || s.to);

  return (
    <div className="rounded-lg border p-4 space-y-3 bg-muted/20">
      <div className="flex items-center gap-4">
        <span className="text-sm font-medium">Exibição</span>
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input type="radio" checked={!scheduled} onChange={() => onChange(null)} />
          <InfinityIcon className="h-3.5 w-3.5 text-muted-foreground" />
          Sempre
        </label>
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input
            type="radio"
            checked={scheduled}
            onChange={() => onChange({ start: todayIso(), end: todayIso(6) })}
          />
          <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
          Agendada
        </label>
      </div>

      {scheduled && (
        <>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-muted-foreground mr-1">Atalhos:</span>
            {PRESETS.map(p => (
              <button
                key={p.label}
                type="button"
                onClick={() => onChange(p.build())}
                className="text-xs px-2 py-1 rounded-md border border-input bg-background hover:border-brand-400 hover:bg-muted transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">De</label>
              <input
                type="date"
                value={s.start ?? ''}
                onChange={e => set({ start: e.target.value || undefined })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Até (vazio = não expira)</label>
              <input
                type="date"
                value={s.end ?? ''}
                min={s.start || undefined}
                onChange={e => set({ end: e.target.value || undefined })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground">
              Dias da semana <span className="opacity-70">(nenhum marcado = todos os dias)</span>
            </label>
            <div className="flex gap-1.5 mt-1">
              {WEEKDAY_LABELS.map((label, i) => {
                const on = days.includes(i);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => set({ days: on ? days.filter(d => d !== i) : [...days, i] })}
                    className={`h-9 w-11 rounded-md border text-xs font-medium transition-all ${
                      on
                        ? 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-300'
                        : 'border-input bg-background text-muted-foreground hover:border-brand-300'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <details open={hasTime}>
            <summary className="text-xs text-muted-foreground cursor-pointer select-none">
              Faixa de horário (opcional)
            </summary>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <label className="text-xs text-muted-foreground">Das</label>
                <input
                  type="time"
                  value={s.from ?? ''}
                  onChange={e => set({ from: e.target.value || undefined })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Às</label>
                <input
                  type="time"
                  value={s.to ?? ''}
                  onChange={e => set({ to: e.target.value || undefined })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                />
              </div>
            </div>
          </details>
        </>
      )}

      {/* Frase-resumo: é o que o usuário lê pra conferir se acertou. */}
      <div className="flex flex-wrap items-center gap-2 pt-1 border-t">
        <span className="text-sm pt-2">📅 {describeSchedule(value)}</span>
        <span className={`text-xs px-2 py-0.5 rounded-full border mt-2 ${badge.cls}`}>{badge.text}</span>
      </div>
      {scheduled && (
        <p className="text-xs text-muted-foreground">
          A TV aplica a mudança em até 5 minutos, no fuso horário dela.
        </p>
      )}
    </div>
  );
}
